import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { AlertPopup } from './AlertPopup';

const AlertContext = createContext(null);

export const ALERT_TYPES = {
  TASK_COMPLETED: 'task_completed',
  ROOM_OUT_OF_SERVICE: 'room_out_of_service',
  MAINTENANCE_STARTED: 'maintenance_started',
  NEW_COMPLAINT: 'new_complaint',
  CHECKLIST_COMPLETED: 'checklist_completed'
};

// Polling interval in milliseconds (10 seconds for better multi-PC sync)
const POLLING_INTERVAL = 10000;

export function AlertProvider({ children, API, token }) {
  const [alertQueue, setAlertQueue] = useState([]);
  const [currentAlert, setCurrentAlert] = useState(null);

  // Use ref to always have access to latest settings without re-creating showAlert
  const settingsRef = useRef({
    enabled: true,
    soundEnabled: true,
    alerts: {
      task_completed: true,
      room_out_of_service: true,
      maintenance_started: false,
      new_complaint: true,
      checklist_completed: false
    }
  });

  const [settings, setSettings] = useState(settingsRef.current);
  
  // Track seen notification IDs to avoid duplicates
  const seenNotificationsRef = useRef(new Set());
  // Polling interval ref
  const pollingIntervalRef = useRef(null);
  // Settings loaded flag
  const settingsLoadedRef = useRef(false);

  // Fetch settings on mount and when token changes
  useEffect(() => {
    if (token && API) {
      fetchSettings();
    }
  }, [token, API]);
  
  // Start polling when authenticated - with slight delay to let settings load
  useEffect(() => {
    if (token && API) {
      // Small delay to let settings load first
      const startTimer = setTimeout(() => {
        startPolling();
      }, 2000);
      
      return () => {
        clearTimeout(startTimer);
        stopPolling();
      };
    }
    return () => stopPolling();
  }, [token, API]);
  
  // Process alert queue - show next alert when current one closes
  useEffect(() => {
    if (!currentAlert && alertQueue.length > 0) {
      const [next, ...rest] = alertQueue;
      setCurrentAlert(next);
      setAlertQueue(rest);
    }
  }, [currentAlert, alertQueue]);
  
  const startPolling = () => {
    stopPolling();
    
    // Initial fetch
    fetchPendingNotifications();
    
    // Set up polling interval
    pollingIntervalRef.current = setInterval(() => {
      fetchPendingNotifications();
    }, POLLING_INTERVAL);
  };
  
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };
  
  const fetchPendingNotifications = async () => {
    if (!token || !API) return;
    
    // Check if alerts are enabled (use ref for latest value)
    if (!settingsRef.current.enabled) return;
    
    try {
      const response = await axios.get(`${API}/notifications/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const notifications = response.data?.notifications || [];
      
      // Process only popup_alert types (broadcast alerts)
      notifications.forEach(notification => {
        if (notification.type === 'popup_alert') {
          const notificationKey = `${notification.alert_type}_${notification.created_at}`;
          
          // Skip if we've already seen this notification
          if (seenNotificationsRef.current.has(notificationKey)) {
            return;
          }
          
          // Mark as seen
          seenNotificationsRef.current.add(notificationKey);
          
          // Clean up old entries (keep last 100)
          if (seenNotificationsRef.current.size > 100) {
            const entries = Array.from(seenNotificationsRef.current);
            seenNotificationsRef.current = new Set(entries.slice(-50));
          }
          
          // Trigger the alert popup via queue
          showAlert(notification.alert_type, {
            title: notification.title,
            message: notification.message,
            details: notification.details
          });
        }
      });
    } catch (error) {
      // Silently fail for polling
      if (error.response?.status !== 401) {
        // Don't spam console
      }
    }
  };

  const fetchSettings = async () => {
    if (!token || !API) return;
    
    try {
      const response = await axios.get(`${API}/settings/popup-alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        settingsRef.current = response.data;
        setSettings(response.data);
        settingsLoadedRef.current = true;
      }
    } catch (error) {
      console.error('[AlertContext] Error fetching popup settings:', error);
    }
  };

  const showAlert = useCallback((type, options = {}) => {
    const currentSettings = settingsRef.current;
    
    // Check if alerts are globally enabled
    if (!currentSettings.enabled) return;
    
    // Check if this specific alert type is enabled
    if (!currentSettings.alerts || !currentSettings.alerts[type]) return;

    const newAlert = {
      type,
      title: options.title || '',
      message: options.message || '',
      details: options.details || '',
      soundEnabled: currentSettings.soundEnabled
    };
    
    // If no alert is showing, show immediately; otherwise queue it
    setCurrentAlert(prev => {
      if (!prev) return newAlert;
      // Already showing an alert, add to queue
      setAlertQueue(q => [...q, newAlert]);
      return prev;
    });
  }, []);

  const hideAlert = useCallback(() => {
    setCurrentAlert(null);
  }, []);

  const updateSettings = useCallback((newSettings) => {
    settingsRef.current = newSettings;
    setSettings(newSettings);
  }, []);

  return (
    <AlertContext.Provider value={{ showAlert, hideAlert, settings, updateSettings, fetchSettings }}>
      {children}
      {currentAlert && (
        <AlertPopup
          isOpen={true}
          onClose={hideAlert}
          type={currentAlert.type}
          title={currentAlert.title}
          message={currentAlert.message}
          details={currentAlert.details}
          soundEnabled={currentAlert.soundEnabled}
        />
      )}
    </AlertContext.Provider>
  );
}

export function useAlert() {
  const context = useContext(AlertContext);
  if (!context) {
    console.warn('[useAlert] Used outside AlertProvider, returning no-op');
    return {
      showAlert: () => {},
      hideAlert: () => {},
      settings: { enabled: false, soundEnabled: false, alerts: {} },
      updateSettings: () => {},
      fetchSettings: () => {}
    };
  }
  return context;
}

export default AlertContext;
